/**
 * Custom Hook pour gérer les requêtes API avec cleanup automatique
 * Empêche les mises à jour d'état sur des composants démontés
 */

import { useEffect, useRef, useCallback } from 'react';
import axios from '@/utils/axiosConfig';

/**
 * Hook pour créer un signal d'annulation qui se nettoie automatiquement
 * @returns {object} { signal, isMounted ref }
 */
export const useAbortController = () => {
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Créer un nouveau AbortController
    abortControllerRef.current = new AbortController();
    isMountedRef.current = true;

    // Cleanup: annuler toutes les requêtes en cours lors du démontage
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    signal: abortControllerRef.current?.signal,
    isMounted: isMountedRef
  };
};

/**
 * Hook pour effectuer des requêtes API sécurisées avec cleanup automatique
 * @param {Function} fetchFunction - Fonction async qui effectue la requête
 * @param {Array} dependencies - Dépendances du useEffect
 */
export const useSafeEffect = (fetchFunction, dependencies = []) => {
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    isMountedRef.current = true;

    const executeFetch = async () => {
      try {
        await fetchFunction(abortControllerRef.current.signal, isMountedRef);
      } catch (error) {
        // Ignorer les erreurs d'annulation
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error in safe effect:', error);
        }
      }
    };

    executeFetch();

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, dependencies);
};

/**
 * Wrapper pour axios qui vérifie si le composant est toujours monté
 * @param {Function} setState - Fonction de mise à jour d'état
 * @param {object} isMountedRef - Ref pour vérifier si le composant est monté
 * @returns {Function} - Fonction wrapper qui vérifie isMounted avant setState
 */
export const safeSetter = (setState, isMountedRef) => {
  return (value) => {
    if (isMountedRef.current) {
      setState(value);
    }
  };
};

/**
 * Hook pour créer une instance axios avec AbortController intégré
 * @returns {object} { axiosInstance, signal, isMounted }
 */
export const useSafeAxios = () => {
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Créer une instance axios avec le signal
  const axiosInstance = useCallback(
    (config) => {
      return axios({
        ...config,
        signal: abortControllerRef.current?.signal
      });
    },
    []
  );

  return {
    axiosInstance,
    signal: abortControllerRef.current?.signal,
    isMounted: isMountedRef
  };
};

export default {
  useAbortController,
  useSafeEffect,
  safeSetter,
  useSafeAxios
};
